/**
 * Sorting, filtering and review status for the Labels list (segmentations, references,
 * keypoint collections). Pure functions so the list order shown, the ‹ › stepper and the
 * tests all agree.
 */

export type SortKey = 'date' | 'feature' | 'technician';
export type SortDir = 'asc' | 'desc';
export type StatusFilter = 'all' | 'accepted' | 'rejected' | 'unreviewed';
export type ReviewStatus = Exclude<StatusFilter, 'all'>;

export interface ListPrefs {
  sortKey: SortKey;
  sortDir: SortDir;
  status: StatusFilter;
}

export const DEFAULT_PREFS: ListPrefs = { sortKey: 'date', sortDir: 'desc', status: 'all' };

/** Latest review from the warehouse `review` table, attached by the API. */
export const reviewStatus = (r: any): ReviewStatus => {
  const d = String(r?.review?.decision ?? '').toUpperCase();
  return d === 'ACCEPTED' ? 'accepted' : d === 'REJECTED' ? 'rejected' : 'unreviewed';
};

export const technicianName = (r: any): string => {
  const u = r?.user;
  const full = [u?.first_name, u?.last_name].filter(Boolean).join(' ').trim();
  return full || u?.email || '';
};

export const featureName = (r: any): string =>
  r?.segmentation_class?.value ?? r?.keypoint_collection_class?.value ?? '';

const dateValue = (r: any): number => {
  const t = r?.created_at ? Date.parse(r.created_at) : NaN;
  return Number.isFinite(t) ? t : NaN;
};

/** Stable sort; rows missing the key always go last, whatever the direction. */
export function sortRows<T>(rows: T[], key: SortKey, dir: SortDir): T[] {
  const sign = dir === 'asc' ? 1 : -1;
  const indexed = rows.map((row, i) => ({ row, i }));
  indexed.sort((a, b) => {
    let cmp = 0;
    if (key === 'date') {
      const x = dateValue(a.row);
      const y = dateValue(b.row);
      const xm = Number.isNaN(x);
      const ym = Number.isNaN(y);
      if (xm || ym) return xm === ym ? a.i - b.i : xm ? 1 : -1;
      cmp = x - y;
    } else {
      const get = key === 'feature' ? featureName : technicianName;
      const x = get(a.row).toLowerCase();
      const y = get(b.row).toLowerCase();
      if (!x || !y) return !x === !y ? a.i - b.i : !x ? 1 : -1;
      cmp = x.localeCompare(y, undefined, { numeric: true });
      // Within the same feature/technician, newest first.
      if (cmp === 0) {
        const dx = dateValue(a.row);
        const dy = dateValue(b.row);
        if (!Number.isNaN(dx) && !Number.isNaN(dy) && dx !== dy) return dy - dx;
      }
    }
    return cmp !== 0 ? sign * cmp : a.i - b.i;
  });
  return indexed.map(x => x.row);
}

export function filterByStatus<T>(rows: T[], status: StatusFilter): T[] {
  return status === 'all' ? rows : rows.filter(r => reviewStatus(r) === status);
}

export function statusCounts(rows: any[]): Record<StatusFilter, number> {
  const out: Record<StatusFilter, number> = {
    all: rows.length,
    accepted: 0,
    rejected: 0,
    unreviewed: 0,
  };
  rows.forEach(r => {
    out[reviewStatus(r)] += 1;
  });
  return out;
}

const PREFS_KEY = 'atlas.labels.list';

/** Remembered per browser; storage can be unavailable (private mode), so fall back to defaults. */
export function readPrefs(): ListPrefs {
  try {
    const raw = window.localStorage.getItem(PREFS_KEY);
    if (!raw) return DEFAULT_PREFS;
    const p = JSON.parse(raw);
    return {
      sortKey: ['date', 'feature', 'technician'].includes(p.sortKey)
        ? p.sortKey
        : DEFAULT_PREFS.sortKey,
      sortDir: p.sortDir === 'asc' ? 'asc' : 'desc',
      status: ['all', 'accepted', 'rejected', 'unreviewed'].includes(p.status) ? p.status : 'all',
    };
  } catch {
    return DEFAULT_PREFS;
  }
}

export function writePrefs(p: ListPrefs): void {
  try {
    window.localStorage.setItem(PREFS_KEY, JSON.stringify(p));
  } catch {
    /* ignore */
  }
}
